import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { useCreditCard } from "@/hooks/useCreditCard";
import { GeneratePaymentRequest, VerifyPendingPaymentProps } from "@/models";
import { copyTextToClipboard, formatCurrency, formatFinalNumbers, formatYearMonth } from "@/utils";
import {
    Flex,
    Box,
    Text,
    Stack,
    VStack,
    IconButton,
    Divider,
    HStack,
    Button as ChakraButton,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { BarteIcon, CheckCircleIcon2, CloseCircleIcon, CopyIcon } from "@/styles/icons";
import { QRCodeSVG } from "qrcode.react";
import { PixMiniIcon } from "@/styles/icons/PixMiniIcon";
import { useRedeem } from "@/hooks";
import { Button, Checkbox, Drawer, Loading } from "barte-design-system";


interface DrawerPaymentInvoiceProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CreditCardInvoiceReadyProps {
    maskedPan: string;
    cardName: string;
    invoiceStatus: string;
    totalAmount: number;
    cardInvoiceId: number;
    yearMonth: string;
}

type PaymentMethodOptions = "PIX" | "CHECKING_ACCOUNT"

export function DrawerPaymentInvoice({
    isOpen,
    onClose,
}: DrawerPaymentInvoiceProps) {
    const [selectedInvoice, setSelectedInvoice] = useState<number[]>([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodOptions>("CHECKING_ACCOUNT");
    const { 
        getInvoicesReady, 
        getInvoicesPendingPayment, 
        postGeneratePayment 
    } = useCreditCard();
    const { getBalance } = useRedeem();
    const { execute: getInvoicesReadyPayment, loading: isLoadingInvoicesReadyPayment } = useAsyncFunction(getInvoicesReady);
    const { execute: getExecutePendingPaymentInvoice, loading: isLoadingPendingPaymentInvoice } = useAsyncFunction(getInvoicesPendingPayment);
    const { execute: postGeneratePaymentExecute, loading: isLoadingPostGeneratePayment } = useAsyncFunction(postGeneratePayment);
    const { execute: getBalanceExecute, loading: isLoadingGetBalance } = useAsyncFunction(getBalance);
    const [invoicesReady, setInvoicesReady] = useState<CreditCardInvoiceReadyProps[]>([]);
    const [pendingPayment, setPendingPayment] = useState({} as VerifyPendingPaymentProps);
    const [debitAccountBalance, setDebitAccountBalance] = useState<number>(0);
    const [section, setSection] = useState("choose-payment");
    const getInitialData = async () => {
        const response = await getExecutePendingPaymentInvoice();
        if(response.data) {
            setPendingPayment(response.data);
            setSection("pending-payment");
            return;
        } 
        const response_ready_payment = await getInvoicesReadyPayment();
        setInvoicesReady(response_ready_payment.data);
    }
    const getBalanceInitialData = async () => {
        const response = await getBalanceExecute();
        if(response.data) {
            setDebitAccountBalance(response.data.debitAccountAmount);
        }
    }
    const grouped = useMemo(() => {
        return invoicesReady.reduce<Record<string, CreditCardInvoiceReadyProps[]>>((acc, curr) => {
          if (!acc[curr.yearMonth]) acc[curr.yearMonth] = [];
          acc[curr.yearMonth].push(curr);
          return acc;
        }, {});
    }, [invoicesReady]);
    
    const toggleCard = (id: number) => {
        setSelectedInvoice((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleAll = (ids: number[]) => {
        const allSelected = ids.every((id) => selectedInvoice.includes(id));
        if (allSelected) {
            setSelectedInvoice((prev) => prev.filter((id) => !ids.includes(id)));
        } else {
            setSelectedInvoice((prev) => [...new Set([...prev, ...ids])]);
        }
    };

    const totalSelected = useMemo(() => {
        return invoicesReady
            .filter((c) => selectedInvoice.includes(c.cardInvoiceId))
            .reduce((sum, c) => sum + c.totalAmount, 0);
    }, [selectedInvoice]);

    const totalInvoice = useMemo(() => {
        return invoicesReady.reduce((sum, c) => sum + c.totalAmount, 0);
    }, [invoicesReady]);

    const formatDrawerTitle = (section: string) => {
        const formatTitle = {
            "choose-payment": "Pagar fatura",
            "choose-payment-method": "Pagar fatura",
            "pending-payment": "Pagar fatura",
            "payment-with-account": "Confirmar pagamento",
            "payment-success": "Fatura paga",
            "payment-error": "Fatura paga"
        }
        return formatTitle[section] || "Pagar fatura"
    }

    const handleSubmit = async () => {
        try {
            const formData: GeneratePaymentRequest = {
                cardInvoiceIds: selectedInvoice,
                paymentMethod: selectedPaymentMethod,
            }
            const response = await postGeneratePaymentExecute(formData);
            if(selectedPaymentMethod === "PIX") {
                setSection("pending-payment");
                setPendingPayment(response.data);
                return
            }
            setSection("payment-success");
        } catch {
            setSection("payment-error");
        } 
        
    }

    useEffect(() => {
        if(isOpen) {
            getInitialData();
        }
    }, [isOpen])
    useEffect(() => {
        if(section === "choose-payment-method") {
            getBalanceInitialData();
            if(totalSelected > debitAccountBalance) {
                setSelectedPaymentMethod("PIX");
            }
        }
    }, [section])
    return (
        <Drawer
            title={formatDrawerTitle(section)}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                section === "choose-payment"
                    ? {
                        secondaryButton: { children: "Cancelar", variant: "outline", onClick: onClose },
                        primaryButton: {
                            children: "Continuar",
                            onClick: () => setSection("choose-payment-method"),
                            loading: isLoadingPostGeneratePayment,
                            disabled: selectedInvoice.length === 0,
                        },
                    }
                    : section === "choose-payment-method"
                    ? {
                        secondaryButton: { children: "Voltar", variant: "outline", onClick: () => setSection("choose-payment") },
                        primaryButton: {
                            children: "Continuar",
                            onClick: handleSubmit,
                            disabled: isLoadingPostGeneratePayment || (selectedPaymentMethod === "CHECKING_ACCOUNT" && totalSelected > debitAccountBalance),
                            loading: isLoadingPostGeneratePayment,
                        },
                    }
                    : section === "pending-payment"
                    ? { primaryButton: { children: "Já paguei", variant: "outline", onClick: onClose } }
                    : { primaryButton: { children: "Fechar", variant: "outline", onClick: onClose } }
            }
        >
                    <Flex 
                        direction="column"
                        bg="white" 
                        borderRadius="lg" 
                        w="100%"
                    >
                        {(isLoadingGetBalance || isLoadingInvoicesReadyPayment || isLoadingPendingPaymentInvoice) ? (
                            <Flex
                                w="100%"
                                align="center"
                                justify="center"
                                mt="4"
                            >
                                <Loading type="page" />
                            </Flex>
                        ) : (
                            <>
                                {section === "choose-payment" && (  
                                <Flex
                                    direction="column"
                                    gap={4}
                                >
                                    { /* Header */}
                                    <Flex 
                                        w="100%" 
                                        justify="space-between" 
                                        mb={4}
                                        p={4}
                                    >
                                    <Box>
                                        <Text fontSize="2xl" fontWeight="bold">
                                            {formatCurrency({ value: totalInvoice })}
                                        </Text>
                                        <Text color="gray.500">Valor total da fatura</Text>
                                    </Box>
                                    <Box textAlign="right">
                                        <Text fontSize="2xl" fontWeight="bold" color="pink.200">
                                            {formatCurrency({ value: totalSelected })}
                                        </Text>
                                        <Text color="gray.500">Total selecionado a pagar</Text>
                                    </Box>
                                    </Flex>
                                    {/* Button Select All */}
                                    <Flex 
                                        w="100%"
                                        justify="space-between"
                                        align="center" 
                                        p={4}
                                    >
                                        <Text
                                            fontSize="14px"
                                        >
                                            Selecione os cartões que deseja pagar
                                        </Text>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => toggleAll(invoicesReady.map((i) => i.cardInvoiceId))}
                                        >
                                            Selecionar todos
                                        </Button>
                                    </Flex>
                                    {/* Groups */}
                                    <Stack 
                                        p={4} 
                                        spacing={6}
                                    >
                                    {Object.entries(grouped).map(([ym, cards]) => (
                                        <Box key={ym}>
                                        <Flex 
                                            align="center" 
                                            justify="space-between"
                                            borderTop="1px solid" 
                                            borderRight="1px solid"
                                            borderLeft="1px solid"
                                            borderColor="gray.200" 
                                            borderTopRadius="8px" 
                                            bgColor="gray.10"
                                            px={2}
                                            py={2}
                                        >
                                            <Text fontWeight="bold" fontSize="lg">
                                                {formatYearMonth(ym)}
                                            </Text>
                                            <Flex
                                                align="center"
                                                gap="2"
                                            >
                                                {cards[0].invoiceStatus === "CLOSED" ? "Fechada" : "Atrasada"}
                                            </Flex>
                                        </Flex>

                                        <Box 
                                            border="1px solid" 
                                            borderColor="gray.200" 
                                        >
                                            <Flex
                                                px={2}
                                                py={2}
                                                borderBottom="1px solid"
                                                borderColor="gray.100"
                                                fontSize="sm"
                                                fontWeight="medium"
                                                color="gray.500"
                                                gap="4"
                                            >
                                                <Box flex="1">
                                                    Cartão
                                                </Box>
                                                <Box flex="2">
                                                    Nome
                                                </Box>
                                                <Box 
                                                    flex="1" 
                                                    textAlign="right"
                                                >
                                                    Valor
                                                </Box>
                                            </Flex>

                                            {cards.map((card, idx) => (
                                            <Flex
                                                key={card.cardInvoiceId}
                                                px={2}
                                                py={2}
                                                align="center"
                                                borderBottom={
                                                    idx < cards.length - 1 ? "1px solid" : undefined
                                                }
                                                borderColor="gray.100"
                                                gap="4"
                                            >
                                                    <Checkbox
                                                        checked={selectedInvoice.includes(card.cardInvoiceId)}
                                                        onChange={() => toggleCard(card.cardInvoiceId)}                 
                                                        label={`final ${formatFinalNumbers(card.maskedPan)}`}
                                                    />
                                                      
                                                    <Text 
                                                        fontSize="14px"
                                                    >
                                                        {card.cardName}
                                                    </Text>
                                                <Box 
                                                    flex="1" 
                                                    textAlign="right" 
                                                >
                                                    <Text 
                                                        fontSize="14px"
                                                    >
                                                        {formatCurrency({ value: card.totalAmount })}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                            ))}
                                        </Box>
                                        </Box>
                                    ))}
                                    </Stack>
                                </Flex>
                                )}
                                {section === "choose-payment-method" && (
                                    <>
                                        <Flex
                                            direction="column" 
                                            gap={4}
                                        >
                                            <Flex 
                                                direction="column" 
                                                align="center" 
                                                p={4} 
                                                gap={4}
                                            >
                                                {/* Cabeçalho com valores */}
                                                <VStack spacing={0}>
                                                    <Text fontSize="lg" color="gray.700">
                                                    Você está pagando
                                                    </Text>
                                                    <Text fontSize="3xl" fontWeight="bold">
                                                        {formatCurrency({ value: totalSelected })}
                                                    </Text>
                                                    <Text fontSize="sm" color="gray.500">
                                                        Valor total da fatura {formatCurrency({ value: totalInvoice })}
                                                    </Text>
                                                </VStack>
                                                {/* Botões de pagamento */}
                                                <VStack w="full" spacing={3}>
                                                    <ChakraButton
                                                        w="full"
                                                        variant="outline"
                                                        justifyContent="flex-start"
                                                        h="80px"
                                                        onClick={() => setSelectedPaymentMethod("CHECKING_ACCOUNT")}
                                                        borderColor={selectedPaymentMethod === "CHECKING_ACCOUNT" ? "pink.200" : ""}
                                                        isDisabled={totalSelected > debitAccountBalance}
                                                    >
                                                        <Flex 
                                                            w="100%"
                                                            align="center"
                                                            justify="center"
                                                        >
                                                            <HStack spacing={3}
                                                                color={selectedPaymentMethod === "CHECKING_ACCOUNT" ? "pink.200" : ""}
                                                            >
                                                                <BarteIcon 
                                                                    color={selectedPaymentMethod === "CHECKING_ACCOUNT" ? "#CB2159" : "#000"}
                                                                />
                                                                <Flex
                                                                    flexDirection="column"
                                                                    align="center"
                                                                    
                                                                    gap="2"
                                                                >
                                                                    <Text 
                                                                        fontWeight="medium"
                                                                    >
                                                                        Pagar com saldo da conta
                                                                    </Text>
                                                                    <Flex
                                                                        gap="1"
                                                                    >
                                                                        <Text 
                                                                            fontSize="12px"
                                                                            fontWeight={400}
                                                                        >
                                                                            Saldo total {formatCurrency({ value: debitAccountBalance })}
                                                                        </Text>
                                                                        {totalSelected > debitAccountBalance && (
                                                                            <Text
                                                                                fontSize="12px"
                                                                                fontWeight={400}
                                                                            >
                                                                                não é suficiente
                                                                            </Text>
                                                                        )}
                                                                    </Flex>
                                                                </Flex>
                                                            </HStack>
                                                        </Flex>
                                                    </ChakraButton>

                                                    <ChakraButton
                                                        w="full"
                                                        variant="outline"
                                                        justifyContent="flex-start"
                                                        h="80px"
                                                        onClick={() => setSelectedPaymentMethod("PIX")}
                                                        borderColor={selectedPaymentMethod === "PIX" ? "pink.200" : ""}
                                                    >
                                                        <Flex 
                                                            w="100%"
                                                            align="center"
                                                            justify="center"
                                                            gap="3"
                                                            color={selectedPaymentMethod === "PIX" ? "pink.200" : ""}
                                                        >
                                                            <PixMiniIcon 
                                                                size={24}
                                                                color={selectedPaymentMethod === "PIX" ? "#CB2159" : "#000"}
                                                            />
                                                            <Text fontWeight="medium">Pagar com Pix</Text>
                                                        </Flex>
                                                    </ChakraButton>
                                                </VStack>
                                            </Flex>
                                        </Flex>
                                    </>
                                )}
                                {section === "pending-payment" && (
                                    <Flex
                                        direction="column" 
                                        gap={4}
                                    >
                                        <Flex 
                                            direction="column" 
                                            align="center" 
                                            gap={4}
                                            p={4}
                                        >
                                            {/* Título e valores */}
                                            <VStack spacing={0}>
                                                <Text fontSize="lg" color="gray.700">
                                                    Você tem um pagamento de fatura pendente!
                                                </Text>
                                            </VStack>

                                            {/* QR Code */}
                                            <Box p={4} borderWidth={1} borderRadius="md">
                                                <QRCodeSVG value={pendingPayment.emv} size={160} />
                                            </Box>

                                            {/* Código PIX */}
                                            <Flex
                                                w="full"
                                                align="center"
                                                justify="space-between"
                                                borderWidth={1}
                                                borderRadius="md"
                                                p={3}
                                                gap={2}
                                            >
                                                <Text fontSize="xs" noOfLines={1} flex={1}>
                                                    {pendingPayment.emv}
                                                </Text>
                                                <IconButton
                                                    aria-label="Copiar código Pix"
                                                    icon={<CopyIcon />}
                                                    size="sm"
                                                    onClick={() => copyTextToClipboard(pendingPayment.emv)}
                                                />
                                            </Flex>

                                            {/* Validade */}
                                            <Text fontSize="xs" color="gray.500">
                                                Esse QR code tem validade de 24 horas.
                                            </Text>
                                        </Flex>
                                    </Flex>
                                )}
                                {section === "payment-error" && (
                                    <>
                                        <Flex
                                            w="100%"
                                            h="100%"
                                            align="center"
                                            justify="center"
                                            flexDir="column"
                                            gap="4"
                                        >
                                            <CloseCircleIcon />
                                            <Text
                                                maxW="268px"
                                                w="100%"
                                                fontSize="20px"
                                                textAlign="center"
                                            >
                                                Houve um erro no pagamento da sua fatura.
                                            </Text>
                                            <Text
                                                fontSize="14px"
                                            >
                                                Tente novamente
                                            </Text>
                                        </Flex>
                                    </>
                                )}
                                {section === "payment-success" && (
                                    <>
                                        <Flex
                                            w="100%"
                                            h="100%"
                                            align="center"
                                            justify="center"
                                            flexDir="column"
                                            gap="4"
                                            p={4}
                                        >
                                            <CheckCircleIcon2 />
                                            <Text
                                                maxW="268px"
                                                w="100%"
                                                fontSize="20px"
                                                textAlign="center"
                                            >
                                                Você realizou o pagamento de <strong>{formatCurrency({ value: totalSelected })}</strong> da sua fatura
                                            </Text>
                                        </Flex>
                                    </>
                                )}
                            </>
                        )}
                        
                        
                    </Flex>
        </Drawer>
    );
}